import { isNative } from '../platform.ts';

/**
 * Native: sessionstoken (better-auth bearer) per profil i plattformens säkra lagring – iOS Keychain,
 * Android Keystore-krypterad lagring – via @aparajita/capacitor-secure-storage. Aldrig i localStorage.
 * Webb: ingen token alls; sessionen ligger i en httpOnly-cookie som JavaScript inte kan läsa.
 *
 * En token för en utloggning som servern ännu inte bekräftat (offline) ligger kvar i säker lagring
 * under en separat nyckel och används ENDAST för att återkalla sessionen.
 */
const sessionKey = (profileId: string) => `autoday.session.${profileId}`;
const revokeKey = (profileId: string) => `autoday.revoke.${profileId}`;
/** Nyckel från versionen före profilerna. */
const LEGACY_KEY = 'autoday.session';

async function storage() {
  const m = await import('@aparajita/capacitor-secure-storage');
  // Capacitor's plugin proxy exposes a synthetic `then` method. Returning the
  // proxy directly from an async function makes Promise resolution call that
  // method instead of handing the plugin to our caller.
  return { plugin: m.SecureStorage };
}

async function read(key: string): Promise<string | null> {
  if (!isNative()) return null;
  try {
    const v = await (await storage()).plugin.get(key);
    return typeof v === 'string' ? v : null;
  } catch {
    return null;
  }
}

export async function getToken(profileId: string): Promise<string | null> {
  return (await read(sessionKey(profileId))) ?? (profileId === 'acct_legacy' ? await read(LEGACY_KEY) : null);
}

export async function setToken(profileId: string, token: string): Promise<void> {
  if (!isNative()) return;
  await (await storage()).plugin.set(sessionKey(profileId), token);
}

export async function clearToken(profileId: string): Promise<void> {
  if (!isNative()) return;
  const { plugin: s } = await storage();
  await s.remove(sessionKey(profileId)).catch(() => undefined);
  if (profileId === 'acct_legacy') await s.remove(LEGACY_KEY).catch(() => undefined);
}

/** Offline-utloggning: behåll token säkert enbart för senare återkallelse. */
export async function moveTokenToRevocation(profileId: string): Promise<void> {
  if (!isNative()) return;
  const t = await getToken(profileId);
  if (t) await (await storage()).plugin.set(revokeKey(profileId), t);
  await clearToken(profileId);
}

export async function getRevocationToken(profileId: string): Promise<string | null> {
  return read(revokeKey(profileId));
}

export async function clearRevocationToken(profileId: string): Promise<void> {
  if (!isNative()) return;
  await (await storage()).plugin.remove(revokeKey(profileId)).catch(() => undefined);
}
